import Styles from './WriterTopBar.module.scss'
import { UnlockButton } from './UnlockButton'
import { Breadcrumbs } from 'common/Breadcrumbs/Breadcrumbs'
import { FileMenuButton } from './FileMenuButton'
import { ShareButton } from './ShareButton'
import { SearchIconButton } from './SearchIconButton'
import { RightColumnLockButton } from './RightColumnLockButton'
import { observer } from 'mobx-react-lite'
import { useLayout } from 'stores/LayoutStore'
import { CanView } from 'components/Can'
import { ArticleView, UserRole } from 'types'
import { TryBlazeButton } from './TryBlazeButton'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { Button } from './Button'
import { PropsWithChildren } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { FavoriteButton } from './FavoriteButton'
import cx from 'classnames'
import { useHover } from '@react-aria/interactions'
import { DocPresenceAvatars } from 'components/WorkspaceDashboardSearchBar/components/DocPresenceAvatars/DocPresenceAvatars'
import { ScheduledPostStatusTag } from 'components/ScheduledPostStatusTag/ScheduledPostStatusTag'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { CalendarButton } from './CalendarButton'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { BackButton } from './BackButton'
import { CalendarItemStatusTag } from 'components/CalendarItemStatusTag/CalendarItemStatusTag'

export const PlaceHolderButton = ({ children }: PropsWithChildren) => {
  return <Button icon={() => null}>{children}</Button>
}

export const FileMenuButtonButtonWithPlaceholder = () => {
  const articleMeta = useCurrentArticleMeta()

  if (!articleMeta) {
    return <PlaceHolderButton />
  }

  return <FileMenuButton articleId={articleMeta.id} />
}

export const ShareButtonButtonWithPlaceholder = () => {
  const articleMeta = useCurrentArticleMeta()

  if (!articleMeta) {
    return (
      <PlaceHolderButton>
        <span className={Styles.blankText}>Share</span>
      </PlaceHolderButton>
    )
  }

  return <ShareButton articleId={articleMeta.id} />
}

const CenterButtons = () => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const articleMeta = useCurrentArticleMeta()
  const scheduledPostId = articleMeta?.almanac_file.scheduled_post_id
  const { isHovered, hoverProps } = useHover({})
  const { isChannelPostable } = useIsChannelPostable()

  return (
    <div className={cx(Styles.buttonGroup, Styles.centerButtons)} {...hoverProps}>
      <Breadcrumbs />
      {articleMeta && <DocPresenceAvatars articleId={articleMeta.id} />}
      {articleMeta?.id && isChannelPostable(articleMeta.channel) && (
        <>
          {isSystemRedesignEnabled ? (
            <CalendarItemStatusTag almanacFileId={articleMeta.id} />
          ) : (
            <ScheduledPostStatusTag scheduledPostId={scheduledPostId} articleId={articleMeta.id} />
          )}
        </>
      )}
      {isSystemRedesignEnabled && (
        <div className={Styles.shareAndOptions}>
          {articleMeta && (
            <CanView
              as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
              inWorkspace={articleMeta.company_id}
            >
              <ShareButtonButtonWithPlaceholder />
            </CanView>
          )}
          <FileMenuButtonButtonWithPlaceholder />
        </div>
      )}
      {articleMeta && (
        <CanView
          as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
          inWorkspace={articleMeta?.company_id}
        >
          <FavoriteButton article={articleMeta} isVisible={isHovered} />
        </CanView>
      )}
    </div>
  )
}

export const WriterTopBar = observer(() => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const layout = useLayout()
  const articleMeta = useCurrentArticleMeta()
  const { isMobile } = useResponsiveDesign()
  const articleView = useArticleView()

  return (
    <div className={Styles.wrapper} data-testid="writer-top-bar">
      <div className={Styles.leftButtons}>
        {articleMeta && (
          <CanView
            as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
            inWorkspace={articleMeta.company_id}
          >
            <div className={Styles.buttonGroup}>
              <UnlockButton />
              {isSystemRedesignEnabled ? <BackButton iconOnly /> : <SearchIconButton />}
              <CalendarButton iconOnly={isSystemRedesignEnabled} />
            </div>
          </CanView>
        )}
      </div>

      <CenterButtons />

      <div className={Styles.rightButtons}>
        <div className={Styles.buttonGroup}>
          {!isSystemRedesignEnabled && (
            <>
              <FileMenuButtonButtonWithPlaceholder />

              {articleMeta && (
                <CanView
                  as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
                  inWorkspace={articleMeta.company_id}
                >
                  <ShareButtonButtonWithPlaceholder />
                </CanView>
              )}
            </>
          )}
          {articleMeta && (
            <CanView as={[UserRole.UNAUTHED]} inWorkspace={articleMeta.company_id}>
              <TryBlazeButton />
            </CanView>
          )}
          {!layout.rightColumnLocked && !isMobile && articleView && articleView !== ArticleView.Reading && (
            <RightColumnLockButton />
          )}
        </div>
      </div>
    </div>
  )
})
